import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService, ConfirmEventType } from 'primeng/api';
import { JobStatus } from '../shared/models/JobStatus';
import { JobType } from '../shared/models/JobType';
import { ProgressBarStatus } from '../shared/models/ProgressBarStatus';
import { DWSMJob } from './models/DWSMJob';
import { FileTableItemDTO } from './models/FileTableItemDTO';
import { startRedirectProcessForSingleFileUpload, startRedirectProcessForTMOAndMultipleFilesUpload } from './state/dwsm.actions';
import { TVGDescriptionDTO } from 'src/app/dwsm/models/TVGDescriptionDTO';
import { DWSMTaskLengthData } from 'src/app/dwsm/models/DWSMTaskLengthData';
import { DwsmResultVariant } from './models/DwsmResultVariant';

@Injectable()
export class DWSMUtils {
    constructor(
        private readonly store: Store,
        private readonly translateService: TranslateService,
        private readonly confirmationService: ConfirmationService
    ) {}

    public static readonly VISIBILITY_SCORE_THRESHOLD = 95;

    public resolveTaskLengthOverride(
        jobType: string | undefined,
        dwsmTaskLengthData: DWSMTaskLengthData | undefined,
        onProceed: (overwriteTaskLength: boolean) => void,
        fileCount?: number,
        visibilityScore?: number | null,
        onLowVisibilityScore?: () => void
    ): void {
        if (jobType === JobType.DWSM_LITE && visibilityScore != null && visibilityScore < DWSMUtils.VISIBILITY_SCORE_THRESHOLD) {
            onLowVisibilityScore?.();
            return;
        }
        this.resolveRedirectBasedOnTaskLength(jobType, dwsmTaskLengthData, onProceed, fileCount);
    }

    private resolveRedirectBasedOnTaskLength(
        jobType: string | undefined,
        dwsmTaskLengthData: DWSMTaskLengthData | undefined,
        onProceed: (overwriteTaskLength: boolean) => void,
        fileCount?: number
    ): void {
        if (jobType === JobType.DWSM_LITE) {
            onProceed(!!dwsmTaskLengthData?.analysisTaskLength);
            return;
        }
        const hasTaskLengthMismatch = !!dwsmTaskLengthData?.analysisTaskLength && jobType === JobType.DWSM;
        const passesFileCountCheck = fileCount === undefined || (!!fileCount && fileCount <= 1);
        if (hasTaskLengthMismatch && passesFileCountCheck) {
            this.openTaskLengthOverridePopup(
                dwsmTaskLengthData,
                () => onProceed(false),
                () => onProceed(true)
            );
        } else {
            onProceed(false);
        }
    }

    public initiateRedirectOnJobProcessed(
        dwsmJob: DWSMJob,
        uploadFilesName: string[],
        markersPresent: boolean,
        overwriteTaskLength: boolean,
        variant?: DwsmResultVariant
    ): void {
        switch (dwsmJob?.type) {
            case JobType.DWSM:
            case JobType.DWSM_LITE:
                if (uploadFilesName.length === 1 && !markersPresent) {
                    this.store.dispatch(startRedirectProcessForSingleFileUpload({ dwsmJobId: dwsmJob?.id, overwriteTaskLength, variant }));
                } else {
                    this.store.dispatch(
                        startRedirectProcessForTMOAndMultipleFilesUpload({
                            dwsmJobId: dwsmJob?.id,
                            jobType: dwsmJob?.type,
                            overwriteTaskLength,
                            variant
                        })
                    );
                }
                break;
            case JobType.DWSM_TMO:
            case JobType.DWSM_LITE_TMO:
                this.store.dispatch(
                    startRedirectProcessForTMOAndMultipleFilesUpload({
                        dwsmJobId: dwsmJob?.id,
                        jobType: dwsmJob?.type,
                        overwriteTaskLength: false
                    })
                );
                break;
        }
    }

    public mapStatusToProgressBarStatusDWSM(status: string, numberOfFiles: number, fileSize: string): ProgressBarStatus {
        switch (status) {
            case JobStatus.UPLOADING: {
                let uploadingStatus = { ...this.progressBarStatus.UPLOADING_STATUS };
                uploadingStatus.stepDescription += ' ' + numberOfFiles;
                uploadingStatus.stepSecondDescription += ' ' + fileSize;
                return uploadingStatus;
            }
            case JobStatus.NEW:
            case JobStatus.QUEUED:
                return this.progressBarStatus.QUEUED_STATUS;
            case JobStatus.PROCESSING:
                return this.progressBarStatus.PROCESSING_STATUS;
            case JobStatus.PROCESSED:
                return this.progressBarStatus.PROCESSED_STATUS;
            case JobStatus.FAILED:
                return this.progressBarStatus.FAILED_STATUS;
            default:
                return this.progressBarStatus.EMPTY_STATUS;
        }
    }

    public mapStatusToProgressBarStatusDWSMLite(status: string, numberOfFiles: number, fileSize: string): ProgressBarStatus {
        switch (status) {
            case JobStatus.UPLOADING: {
                let uploadingStatus = { ...this.progressBarStatus.UPLOADING_STATUS };
                uploadingStatus.stepDescription += ' ' + numberOfFiles;
                uploadingStatus.stepSecondDescription += ' ' + fileSize;
                return uploadingStatus;
            }
            case JobStatus.NEW:
            case JobStatus.QUEUED_LITE:
                return this.progressBarStatus.QUEUED_STATUS;
            case JobStatus.PROCESSING_LITE:
            case JobStatus.QUEUED:
            case JobStatus.PROCESSING:
                return this.progressBarStatus.PROCESSING_STATUS;
            case JobStatus.PROCESSED:
                return this.progressBarStatus.PROCESSED_STATUS;
            case JobStatus.FAILED:
                return this.progressBarStatus.FAILED_STATUS;
            default:
                return this.progressBarStatus.EMPTY_STATUS;
        }
    }

    public static buildDwsmRecording(fileTableItem: FileTableItemDTO) {
        return {
            fileTableItem: fileTableItem,
            markersData: {
                labels: [''],
                datasets: this.buildDatasets(fileTableItem)
            }
        };
    }

    public static buildDatasets(fileTableItem: FileTableItemDTO) {
        const data = fileTableItem.chartData.data;
        return data.map((value, index) => {
            const colorIndex = index % this.COLORS_ENABLED.length;
            return {
                backgroundColor: fileTableItem.active ? DWSMUtils.COLORS_ENABLED[colorIndex % DWSMUtils.COLORS_ENABLED.length] : this.COLOR_DISABLED,
                borderColor: '#f2f3f4',
                data: [value],
                borderWidth: 2
            };
        });
    }

    public static readonly COLORS_ENABLED: string[] = [
        '#8677FF',
        '#85EEF9',
        '#FFE38E',
        '#E96200',
        '#8D9129',
        '#F6C800',
        '#4ED9C7',
        '#EA7EEC',
        '#3EA359',
        '#007C78',
        '#6800E3',
        '#D50075',
        '#FFB1A2',
        '#FF7AB4',
        '#9FF7B7',
        '#FF2D98',
        '#353D00',
        '#9F9DFF',
        '#FF8761',
        '#6C0061'
    ];
    public static readonly COLOR_DISABLED: string = '#DCDEE1';

    public readonly progressBarStatus = {
        UPLOADING_STATUS: {
            stepStatus: this.translateService.instant('DWSM.PROGRESS_BAR.UPLOAD_STATUS.LBL'),
            stepDescription: this.translateService.instant('DWSM.PROGRESS_BAR.UPLOAD_DESCRIPTION.LBL'),
            stepSecondDescription: this.translateService.instant('DWSM.PROGRESS_BAR.UPLOAD_SECOND_DESCRIPTION.LBL'),
            value: '0',
            isToShowProgress: true,
            isFailed: false
        } as ProgressBarStatus,
        QUEUED_STATUS: {
            stepStatus: this.translateService.instant('DWSM.PROGRESS_BAR.QUEUE_STATUS.LBL'),
            stepDescription: this.translateService.instant('DWSM.PROGRESS_BAR.QUEUE_DESCRIPTION.LBL'),
            stepSecondDescription: '',
            value: '33',
            isToShowProgress: true,
            isFailed: false
        } as ProgressBarStatus,
        PROCESSING_STATUS: {
            stepStatus: this.translateService.instant('DWSM.PROGRESS_BAR.PROCESSING_STATUS.LBL'),
            stepDescription: this.translateService.instant('DWSM.PROGRESS_BAR.PROCESSING_DESCRIPTION.LBL'),
            stepSecondDescription: '',
            value: '66',
            isToShowProgress: true,
            isFailed: false
        } as ProgressBarStatus,
        FAILED_STATUS: {
            stepStatus: this.translateService.instant('ENUM.DWSM.STATUS.FAILED'),
            stepDescription: this.translateService.instant('ERROR.DWSM.PROCESSING.FAILED.TXT'),
            stepSecondDescription: '',
            value: '0',
            isToShowProgress: false,
            isFailed: true
        } as ProgressBarStatus,
        PROCESSED_STATUS: {
            stepStatus: this.translateService.instant('DWSM.PROGRESS_BAR.IMPORT_COMPLETE_STATUS.LBL'),
            stepDescription: '',
            stepSecondDescription: '',
            value: '100',
            isToShowProgress: true,
            isFailed: false
        } as ProgressBarStatus,
        UPLOADING_LITE_TMO_STATUS: {
            stepStatus: this.translateService.instant('PAGE.DWSM_LITE_TMO.UPLOADING_STEP.TXT'),
            stepDescription: '',
            stepSecondDescription: '',
            value: '0',
            isToShowProgress: true,
            isFailed: false
        } as ProgressBarStatus,
        EMPTY_STATUS: {
            stepStatus: '',
            stepDescription: '',
            stepSecondDescription: '',
            value: '0',
            isToShowProgress: false,
            isFailed: false
        } as ProgressBarStatus
    };

    public static getTvgDescription(tvg: TVGDescriptionDTO, userLanguage: string): string | null {
        const englishText = tvg.i18nTextList?.find(elem => elem.textType === 'TVG_TITLE' && elem.languageCode === 'en')?.text;
        const userText = tvg.i18nTextList?.find(elem => elem.textType === 'TVG_TITLE' && elem.languageCode?.toLowerCase() === userLanguage.toLowerCase())?.text;
        return userText ?? englishText ?? tvg.description;
    }

    private openTaskLengthOverridePopup(dwsmTaskLengthData: DWSMTaskLengthData | undefined, onAccept: () => void, onReject?: () => void): void {
        const unitKey = dwsmTaskLengthData?.timeUnitSecond ? 'PAGE.ANALYSIS.TIME_UNIT.SEC.LBL' : 'PAGE.ANALYSIS.TIME_UNIT.MIN.LBL';
        const message = this.translateService.instant('PAGE.DWSM.OVERWRITE_TASK_DURATION_CONFIRMATION.LBL', {
            analysisTaskLength: dwsmTaskLengthData?.analysisTaskLength,
            dwsmTaskLength: dwsmTaskLengthData?.timeUnitSecond
                ? dwsmTaskLengthData?.dwsmTaskLength
                : Math.round((dwsmTaskLengthData?.dwsmTaskLength! / 60) * 100) / 100,
            unit: this.translateService.instant(unitKey).toLowerCase()
        });
        this.confirmationService.confirm({
            key: 'taskLengthOverride',
            message: message,
            accept: () => {
                onAccept();
            },
            reject: (type: ConfirmEventType) => {
                if (type === ConfirmEventType.REJECT) {
                    onReject?.();
                }
            }
        });
    }
}
